// ============================================================
// 01: single source of truth for contract addresses, ABIs,
// block-explorer links, and the bonding-curve math.
// Docs, dapp, and explorer links all read from here (handoff section 8).
//
// The curve and token are live on Ethereum mainnet. The 01 token
// address is pinned to COIN_ADDRESS below (it equals Curve01.coin()).
// The shielded pool and the lender (ShieldedLending) are
// live on mainnet. Supplying, withdrawing, opening a loan, and
// repaying are all wired as real writes; opening and repaying settle
// through the shielded pool's in-browser zk proof flow (collateral
// posted as a proved withdrawal, returned on full repay as a proved
// deposit). USD0 (ShieldedUSD0) is a public ERC-20 dollar backed one
// for one by USDS; mint and redeem are plain public writes, live in the
// dapp. The sUSDS yield on the backing stays with the contract as
// surplus backing, not paid to holders, so each USD0 always redeems for
// one USDS. Making USD0 private is a separate, optional step through the
// USD0 shielded pool from the Private tab.
// ============================================================
import { type Abi, type Address } from "viem";

export const EXPLORER_BASE = "https://etherscan.io";
export const NETWORK_LABEL = "Ethereum mainnet";

// ---- Live mainnet addresses (from the deploy) ----
export const CURVE_ADDRESS: Address = "0x6C1A58ff0751A645Ab0243Dd6967b0338B34f572";
// The 01 token. Pinned to the value returned by Curve01.coin().
export const COIN_ADDRESS: Address = "0x32708538a107253b51a735A724330A23106ca4cA";
export const ROUTER_ADDRESS: Address = "0x46cec41E8c0f30F84c0A520ACa42D780345f8b0B";

// Uniswap deep links for the post-bond secondary market. After the bond the
// curve stops issuing 01, so buying moves to a market like Uniswap and holders
// provide the liquidity themselves. Nothing here is settled inside this app.
export const UNISWAP_SWAP_URL = `https://app.uniswap.org/swap?chain=mainnet&outputCurrency=${COIN_ADDRESS}`;
export const UNISWAP_CREATE_POOL_URL = `https://app.uniswap.org/positions/create?currencyA=${COIN_ADDRESS}`;

export const USDS_ADDRESS: Address = "0xdC035D45d973E3EC169d2276DDab16f1e407384F";
export const SUSDS_ADDRESS: Address = "0xa3931d71877C0E7a3148CB7Eb4463524FEc27fbD";
export const USDC_ADDRESS: Address = "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48";
export const USDT_ADDRESS: Address = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
export const WETH_ADDRESS: Address = "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2";

// ---- Shielded pool (live on Ethereum mainnet) ----
// The pool settles deposits, private transfers, and withdrawals as
// zero-knowledge transactions. It calls the PLONK verifier (through a
// thin wrapper) to check each proof, and the Poseidon hash to build its
// commitment tree. PoseidonT3 source is not verified on Etherscan, but
// the contract is deployed and immutable like the rest.
// These are the upgraded pool and verifier wrapper, the current deploy.
// They replace the previous pool; an earlier pool in this lineage was
// retired after a security issue. Balances and notes do not carry over
// between deploys, so the new tree starts empty and private balances are
// rebuilt from this pool's events. The verifier wrapper now reports its
// proof length as PROOF_BYTES and rejects public inputs outside the field.
export const SHIELDED_POOL_ADDRESS: Address = "0x6F1b5ff473516AA0e56FccA7c590Ee2Fed4dB6E3";
export const VERIFIER_WRAPPER_ADDRESS: Address = "0x1CDF4Cd41fD1363Fd4C5dBC19D7bDF929d42514F";
export const PLONK_VERIFIER_ADDRESS: Address = "0xed2C5Df105814E6CaCd877f2506d2Eb11cdB4E12";
export const POSEIDON_T3_ADDRESS: Address = "0xC77f596372fF3e815456Aa4Ff1794650dAE4385B";

// ---- Lender pool / borrowing (live on Ethereum mainnet) ----
// Suppliers deposit USDS for shares (the idle backing is held as sUSDS,
// so it earns the savings rate) and borrowers post 01 from the shielded
// pool as collateral. supply()/withdraw() are plain USDS in/out and are
// wired as real writes. openLoan()/repay() are real writes too: they
// settle through the shielded pool's in-browser zk proof flow, posting
// collateral as a proved withdrawal and returning it on full repay as a
// proved deposit. Loans are owned by a private ownerCommitment, not an
// address, so a wallet finds its loans by the public USDS recipient.
// Related addresses (coin, curve, pool, usds, sUsds) are readable at
// runtime from the contract, so they are not hardcoded here.
export const SHIELDED_LENDING_ADDRESS: Address = "0x89d1477DEB9e154C6C971C128C4aFE32B6D5D40C";

// ---- USD0 dollar (live on Ethereum mainnet) ----
// USD0 is a normal public ERC-20 dollar, backed one for one by USDS that
// the contract holds as sUSDS. Mint and redeem are plain public swaps:
// mint(usdsAmount, receiver) pulls USDS and issues USD0 one for one, and
// redeem(usd0Amount, minUsdsOut, receiver) burns the caller's USD0 and pays
// USDS back one for one. convertToAssets and convertToShares are pure 1:1,
// so a holder does not earn the sUSDS yield; that yield accrues to the
// contract as surplus backing (over-collateralization). Neither mint nor
// redeem uses a zero-knowledge proof.
//
// Making USD0 private is a separate, optional step: shield public USD0 in
// the USD0 shielded pool from the Private tab, exactly like 01. That pool
// shares the same verifier, hasher, and tree depth as the 01 pool.
//
// The earlier vault settled mint and redeem privately through the pool and
// is retired along with its pool; its notes do not carry over (this pool's
// tree starts empty).
export const SHIELDED_USD0_ADDRESS: Address = "0xcA266c0a778DBB7EceF33226321De6E73cd7b498";
export const USD0_POOL_ADDRESS: Address = "0x2e3C0C685896F67d71481101180c081e25Be76bc";

// ---- Shielded pool registry (Private tab) ----
// The Private tab can shield, send, and unshield in either pool. Both pools
// share the same verifier, hasher, and tree depth, so one in-browser proof
// pipeline serves both; only the pool address and the deposit token differ.
// The 01 pool's deposit token is read at runtime from the contract (curve.coin),
// so its tokenAddress is resolved in the dapp from live stats; the USD0 pool's
// deposit token is the USD0 vault share.
export type ShieldedPoolId = "01" | "usd0";

export interface ShieldedPoolMeta {
  id: ShieldedPoolId;
  poolAddress: Address;
  label: string;
}

export const SHIELDED_POOLS: Record<ShieldedPoolId, ShieldedPoolMeta> = {
  "01": { id: "01", poolAddress: SHIELDED_POOL_ADDRESS, label: "01" },
  usd0: { id: "usd0", poolAddress: USD0_POOL_ADDRESS, label: "USD0" },
};

// Chainlink ETH/USD price feed (mainnet, 8 decimals). Used to estimate the
// USDC value of an ETH buy and to set a real minimum, so an ETH buy is not
// sent with a zero floor.
export const ETH_USD_FEED: Address = "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419";

// Pay assets the Trade panel accepts. USDS goes straight to the curve; the
// others route through the multi-asset router. Decimals matter for parsing
// and for scaling to the curve, which works in 18-decimal USDS terms.
export type PayAsset = "USDS" | "USDC" | "USDT" | "ETH";
export type SellAsset = "USDS" | "USDC";

export const ASSET_DECIMALS: Record<PayAsset, number> = {
  USDS: 18,
  USDC: 6,
  USDT: 6,
  ETH: 18,
};

export function explorerAddressUrl(address: string): string {
  return `${EXPLORER_BASE}/address/${address}`;
}
export function explorerTxUrl(hash: string): string {
  return `${EXPLORER_BASE}/tx/${hash}`;
}

export type ContractStatus = "live" | "pending";

export type ContractInfo = {
  name: string;
  address?: string;
  note?: string;
  description: string;
  status: ContractStatus;
  /** Whether the contract's source is published and verified on Etherscan. */
  verified: boolean;
};

export const CONTRACT_LIST: ContractInfo[] = [
  {
    name: "Curve01",
    address: CURVE_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The bonding curve and the reserve. Mints on buy, burns on sell, holds the reserve as sUSDS, and exposes price, reserve, ratio, and bonding state.",
  },
  {
    name: "01 token",
    address: COIN_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The ERC-20 token. Only Curve01 can mint it. Its address equals the value returned by Curve01.coin().",
  },
  {
    name: "Router",
    address: ROUTER_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The multi-asset router. Lets you buy 01 with ETH, USDC, or USDT, and sell 01 for USDC, by swapping to USDS and routing through the curve in one transaction.",
  },
  {
    name: "USDS",
    address: USDS_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The stablecoin the curve takes in and pays out. Buys are paid in USDS; sells return USDS.",
  },
  {
    name: "sUSDS",
    address: SUSDS_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The yield-bearing form of USDS. The reserve is held here so the idle backing earns the savings rate.",
  },
  {
    name: "ShieldedPool",
    address: SHIELDED_POOL_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The shielded pool for private balances and transfers. Holds 01 deposited into the pool and settles deposits, private transfers, and withdrawals with a proof.",
  },
  {
    name: "VerifierWrapper",
    address: VERIFIER_WRAPPER_ADDRESS,
    status: "live",
    verified: true,
    description:
      "A thin wrapper around the PLONK verifier. The shielded pool calls it to check each proof before a transaction settles.",
  },
  {
    name: "PlonkVerifier",
    address: PLONK_VERIFIER_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The PLONK proof verifier the pool relies on, called through the wrapper.",
  },
  {
    name: "PoseidonT3",
    address: POSEIDON_T3_ADDRESS,
    status: "live",
    verified: false,
    note: "no source, reproducible bytecode",
    description:
      "The Poseidon hash the pool uses to build its commitment tree. It is generated EVM bytecode (circomlib), not Solidity, so there is nothing to source-verify. Confirm it instead by reproduction: the deployed runtime matches poseidonContract.createCode(2) byte for byte, SHA-256 129ab0b2…e6bbe900.",
  },
  {
    name: "ShieldedLending",
    address: SHIELDED_LENDING_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The lender pool and borrowing against 01. Suppliers add USDS for a share of the pool; borrowers post 01 from the shielded pool as collateral, valued by the curve at a conservative, time-averaged price.",
  },
  {
    name: "ShieldedUSD0",
    address: SHIELDED_USD0_ADDRESS,
    status: "live",
    verified: true,
    description:
      "USD0, a public ERC-20 dollar backed one for one by USDS held as sUSDS. Minting issues USD0 one for one and redeeming pays USDS back one for one; the sUSDS yield accrues to the contract as surplus backing, not to holders.",
  },
  {
    name: "ShieldedPool (USD0)",
    address: USD0_POOL_ADDRESS,
    status: "live",
    verified: true,
    description:
      "The optional shielded pool for USD0. Holds USD0 shielded into the pool and settles private transfers and withdrawals with a proof. Separate from the 01 shielded pool, same design.",
  },
];

// ---- ABIs for the reads/writes the dapp uses (live curve) ----

export const curveAbi = [
  { type: "function", name: "coin", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "spotPrice", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "reserveUsds", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "ratio", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "minted", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "bonded", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] },
  { type: "function", name: "S_MAX", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  {
    type: "function",
    name: "quoteBuy",
    stateMutability: "view",
    inputs: [{ name: "usdsIn", type: "uint256" }],
    outputs: [{ type: "uint256" }, { type: "uint256" }, { type: "uint256" }, { type: "uint256" }],
  },
  {
    type: "function",
    name: "quoteSell",
    stateMutability: "view",
    inputs: [{ name: "coinIn", type: "uint256" }],
    outputs: [{ type: "uint256" }, { type: "uint256" }, { type: "uint256" }],
  },
  {
    type: "function",
    name: "buy",
    stateMutability: "nonpayable",
    inputs: [
      { name: "usdsIn", type: "uint256" },
      { name: "minOut", type: "uint256" },
      { name: "to", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "sell",
    stateMutability: "nonpayable",
    inputs: [
      { name: "coinIn", type: "uint256" },
      { name: "minOut", type: "uint256" },
      { name: "to", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
] as const satisfies Abi;

// The 01 token's own view beyond plain ERC-20: its immutable hard cap.
// MAX_SUPPLY() is 21,000,000e18 on chain. The curve is the token's only
// minter and it closed issuance at the bond, so once bonded,
// MAX_SUPPLY - totalSupply() is 01 gone from possible supply for good
// (fee burns plus the never-minted remainder above S_MAX).
export const coinAbi = [
  { type: "function", name: "MAX_SUPPLY", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
] as const satisfies Abi;

export const erc20Abi = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", name: "totalSupply", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
] as const satisfies Abi;

// ---- Multi-asset router: buy with ETH, USDC, or USDT; sell to USDC ----
export const routerAbi = [
  {
    type: "function",
    name: "buyWithUSDC",
    stateMutability: "nonpayable",
    inputs: [
      { name: "usdcIn", type: "uint256" },
      { name: "minCoinOut", type: "uint256" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "buyWithUSDT",
    stateMutability: "nonpayable",
    inputs: [
      { name: "usdtIn", type: "uint256" },
      { name: "minUsdcOut", type: "uint256" },
      { name: "minCoinOut", type: "uint256" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "buyWithETH",
    stateMutability: "payable",
    inputs: [
      { name: "minUsdcOut", type: "uint256" },
      { name: "minCoinOut", type: "uint256" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "sellForUSDC",
    stateMutability: "nonpayable",
    inputs: [
      { name: "coinIn", type: "uint256" },
      { name: "minUsdsOut", type: "uint256" },
      { name: "minUsdcOut", type: "uint256" },
    ],
    outputs: [{ type: "uint256" }],
  },
] as const satisfies Abi;

// ---- Chainlink AggregatorV3 (ETH/USD), for the ETH buy estimate ----
export const aggregatorAbi = [
  {
    type: "function",
    name: "latestRoundData",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "roundId", type: "uint80" },
      { name: "answer", type: "int256" },
      { name: "startedAt", type: "uint256" },
      { name: "updatedAt", type: "uint256" },
      { name: "answeredInRound", type: "uint80" },
    ],
  },
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
] as const satisfies Abi;

// ---- Shielded pool ABIs (verified, from the mainnet deploy) ----
// Reads (tree root, spent nullifiers, token) plus the single transact()
// entry point that settles deposits, private transfers, and withdrawals.
// transact() takes a zk proof built in the browser; wiring that flow
// also needs the circuit witness (.wasm) and proving key (.zkey), which
// are not on chain.
export const shieldedPoolAbi = [{"inputs":[{"internalType":"uint32","name":"_levels","type":"uint32"},{"internalType":"contract IHasher","name":"_hasher","type":"address"},{"internalType":"contract IVerifier","name":"_verifier","type":"address"},{"internalType":"contract IERC20","name":"_token","type":"address"}],"stateMutability":"nonpayable","type":"constructor"},{"inputs":[],"name":"AmountTooLarge","type":"error"},{"inputs":[],"name":"DuplicateCommitment","type":"error"},{"inputs":[],"name":"FeeTooLarge","type":"error"},{"inputs":[],"name":"HashOutOfField","type":"error"},{"inputs":[],"name":"InputAlreadySpent","type":"error"},{"inputs":[],"name":"InvalidExtDataHash","type":"error"},{"inputs":[],"name":"InvalidProof","type":"error"},{"inputs":[],"name":"InvalidPublicAmount","type":"error"},{"inputs":[],"name":"InvalidRoot","type":"error"},{"inputs":[],"name":"LevelsOutOfRange","type":"error"},{"inputs":[],"name":"PoolZeroAddress","type":"error"},{"inputs":[],"name":"PublicInputNotInField","type":"error"},{"inputs":[],"name":"ReentrancyGuardReentrantCall","type":"error"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"SafeERC20FailedOperation","type":"error"},{"inputs":[],"name":"SameCommitment","type":"error"},{"inputs":[],"name":"SameNullifier","type":"error"},{"inputs":[],"name":"TreeFull","type":"error"},{"inputs":[],"name":"ZeroAddress","type":"error"},{"inputs":[],"name":"ZeroRelayer","type":"error"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"commitment","type":"uint256"},{"indexed":true,"internalType":"uint32","name":"index","type":"uint32"},{"indexed":false,"internalType":"bytes","name":"encryptedOutput","type":"bytes"}],"name":"NewCommitment","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"nullifier","type":"uint256"}],"name":"NewNullifier","type":"event"},{"inputs":[],"name":"FIELD_SIZE","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"MAX_EXT_AMOUNT","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"MAX_FEE","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"ROOT_HISTORY_SIZE","outputs":[{"internalType":"uint32","name":"","type":"uint32"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"ZERO_VALUE","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"commitments","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"currentRootIndex","outputs":[{"internalType":"uint32","name":"","type":"uint32"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"filledSubtrees","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"getLastRoot","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"hasher","outputs":[{"internalType":"contract IHasher","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"root","type":"uint256"}],"name":"isKnownRoot","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"nullifierHash","type":"uint256"}],"name":"isSpent","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"levels","outputs":[{"internalType":"uint32","name":"","type":"uint32"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"nextIndex","outputs":[{"internalType":"uint32","name":"","type":"uint32"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"nullifierHashes","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"roots","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"token","outputs":[{"internalType":"contract IERC20","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"components":[{"internalType":"bytes","name":"proof","type":"bytes"},{"internalType":"uint256","name":"root","type":"uint256"},{"internalType":"uint256[2]","name":"inputNullifiers","type":"uint256[2]"},{"internalType":"uint256[2]","name":"outputCommitments","type":"uint256[2]"},{"internalType":"uint256","name":"publicAmount","type":"uint256"},{"internalType":"bytes32","name":"extDataHash","type":"bytes32"}],"internalType":"struct ShieldedPool.Proof","name":"args","type":"tuple"},{"components":[{"internalType":"address","name":"recipient","type":"address"},{"internalType":"int256","name":"extAmount","type":"int256"},{"internalType":"address","name":"relayer","type":"address"},{"internalType":"uint256","name":"fee","type":"uint256"},{"internalType":"bytes","name":"encryptedOutput1","type":"bytes"},{"internalType":"bytes","name":"encryptedOutput2","type":"bytes"}],"internalType":"struct ShieldedPool.ExtData","name":"ext","type":"tuple"}],"name":"transact","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"verifier","outputs":[{"internalType":"contract IVerifier","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"zeros","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"}] as const satisfies Abi;

// The PLONK verifier wrapper the pool calls to check each proof.
export const verifierWrapperAbi = [{"inputs":[{"internalType":"contract IPlonkVerifier","name":"_inner","type":"address"}],"stateMutability":"nonpayable","type":"constructor"},{"inputs":[],"name":"InvalidProofLength","type":"error"},{"inputs":[],"name":"PublicInputNotInField","type":"error"},{"inputs":[],"name":"ZeroVerifier","type":"error"},{"inputs":[],"name":"FIELD_SIZE","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"PROOF_BYTES","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"inner","outputs":[{"internalType":"contract IPlonkVerifier","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"bytes","name":"proof","type":"bytes"},{"internalType":"uint256[7]","name":"publicInputs","type":"uint256[7]"}],"name":"verifyProof","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"}] as const satisfies Abi;

// ---- ShieldedLending ABI (verified, from the mainnet deploy) ----
// Reads (LTV, BPS, RATE_PER_SEC, collateralValue, priceAvg, shares,
// totalShares, totalScaledDebt, borrowIndex, lastAccrual, currentDebt,
// healthFactor, loans) plus the writes. supply()/withdraw() take plain
// USDS / shares and are wired as real writes. openLoan()/repay() take a
// ShieldedPool.Proof + ExtData tuple (a browser-built zk proof) and are
// wired as real writes too: openLoan posts collateral as a proved
// withdrawal, and a full repay returns it as a proved deposit.
export const shieldedLendingAbi = [{"inputs":[{"internalType":"contract Curve01","name":"_curve","type":"address"},{"internalType":"contract ShieldedPool","name":"_pool","type":"address"},{"internalType":"contract IERC20","name":"_usds","type":"address"},{"internalType":"contract IERC4626","name":"_sUsds","type":"address"}],"stateMutability":"nonpayable","type":"constructor"},{"inputs":[],"name":"AmountTooSmall","type":"error"},{"inputs":[],"name":"ExceedsLTV","type":"error"},{"inputs":[],"name":"Healthy","type":"error"},{"inputs":[],"name":"InvalidLoanOwner","type":"error"},{"inputs":[],"name":"LoanTermsNotBound","type":"error"},{"inputs":[],"name":"NotLoanOwner","type":"error"},{"inputs":[],"name":"NotOpen","type":"error"},{"inputs":[],"name":"ReentrancyGuardReentrantCall","type":"error"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"SafeERC20FailedOperation","type":"error"},{"inputs":[],"name":"WiringMismatch","type":"error"},{"inputs":[],"name":"ZeroAddress","type":"error"},{"inputs":[],"name":"ZeroAmount","type":"error"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"loanId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"collateral","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"debt","type":"uint256"},{"indexed":false,"internalType":"address","name":"to","type":"address"}],"name":"Borrowed","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"loanId","type":"uint256"},{"indexed":false,"internalType":"address","name":"liquidator","type":"address"},{"indexed":false,"internalType":"uint256","name":"repaid","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"seized","type":"uint256"}],"name":"Liquidated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"loanId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"},{"indexed":false,"internalType":"bool","name":"closed","type":"bool"}],"name":"Repaid","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"who","type":"address"},{"indexed":false,"internalType":"uint256","name":"usds","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"shares","type":"uint256"}],"name":"Supplied","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"who","type":"address"},{"indexed":false,"internalType":"uint256","name":"usds","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"shares","type":"uint256"}],"name":"Withdrawn","type":"event"},{"inputs":[],"name":"BPS","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"CLOSE_FACTOR","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"HAIRCUT","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"LIQ_BONUS","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"LIQ_THRESHOLD","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"LTV","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"RATE_PER_SEC","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"TWAP_WINDOW","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"VIRTUAL_ASSETS","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"VIRTUAL_SHARES","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"borrowIndex","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"coin","outputs":[{"internalType":"contract IERC20","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"collateralValue","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"}],"name":"currentDebt","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"curve","outputs":[{"internalType":"contract Curve01","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"}],"name":"healthFactor","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"}],"name":"isLiquidatable","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"lastAccrual","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"lastObs","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"},{"internalType":"uint256","name":"repayAmount","type":"uint256"},{"internalType":"address","name":"to","type":"address"}],"name":"liquidate","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"}],"name":"liquidationRisk","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"loans","outputs":[{"internalType":"uint256","name":"collateral","type":"uint256"},{"internalType":"uint256","name":"scaledDebt","type":"uint256"},{"internalType":"uint256","name":"ownerCommitment","type":"uint256"},{"internalType":"bool","name":"open","type":"bool"},{"internalType":"address","name":"owner","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"nextLoanId","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"components":[{"internalType":"bytes","name":"proof","type":"bytes"},{"internalType":"uint256","name":"root","type":"uint256"},{"internalType":"uint256[2]","name":"inputNullifiers","type":"uint256[2]"},{"internalType":"uint256[2]","name":"outputCommitments","type":"uint256[2]"},{"internalType":"uint256","name":"publicAmount","type":"uint256"},{"internalType":"bytes32","name":"extDataHash","type":"bytes32"}],"internalType":"struct ShieldedPool.Proof","name":"args","type":"tuple"},{"components":[{"internalType":"address","name":"recipient","type":"address"},{"internalType":"int256","name":"extAmount","type":"int256"},{"internalType":"address","name":"relayer","type":"address"},{"internalType":"uint256","name":"fee","type":"uint256"},{"internalType":"bytes","name":"encryptedOutput1","type":"bytes"},{"internalType":"bytes","name":"encryptedOutput2","type":"bytes"}],"internalType":"struct ShieldedPool.ExtData","name":"ext","type":"tuple"},{"internalType":"uint256","name":"borrowAmount","type":"uint256"},{"internalType":"address","name":"to","type":"address"},{"internalType":"address","name":"owner","type":"address"},{"internalType":"uint256","name":"ownerCommitment","type":"uint256"}],"name":"openLoan","outputs":[{"internalType":"uint256","name":"loanId","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"pool","outputs":[{"internalType":"contract ShieldedPool","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"priceAvg","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"loanId","type":"uint256"},{"internalType":"uint256","name":"amount","type":"uint256"},{"components":[{"internalType":"bytes","name":"proof","type":"bytes"},{"internalType":"uint256","name":"root","type":"uint256"},{"internalType":"uint256[2]","name":"inputNullifiers","type":"uint256[2]"},{"internalType":"uint256[2]","name":"outputCommitments","type":"uint256[2]"},{"internalType":"uint256","name":"publicAmount","type":"uint256"},{"internalType":"bytes32","name":"extDataHash","type":"bytes32"}],"internalType":"struct ShieldedPool.Proof","name":"reshieldArgs","type":"tuple"},{"components":[{"internalType":"address","name":"recipient","type":"address"},{"internalType":"int256","name":"extAmount","type":"int256"},{"internalType":"address","name":"relayer","type":"address"},{"internalType":"uint256","name":"fee","type":"uint256"},{"internalType":"bytes","name":"encryptedOutput1","type":"bytes"},{"internalType":"bytes","name":"encryptedOutput2","type":"bytes"}],"internalType":"struct ShieldedPool.ExtData","name":"reshieldExt","type":"tuple"}],"name":"repay","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"sUsds","outputs":[{"internalType":"contract IERC4626","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"shares","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"supply","outputs":[{"internalType":"uint256","name":"s","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"totalScaledDebt","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"totalShares","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"usds","outputs":[{"internalType":"contract IERC20","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"s","type":"uint256"}],"name":"withdraw","outputs":[{"internalType":"uint256","name":"amount","type":"uint256"}],"stateMutability":"nonpayable","type":"function"}] as const satisfies Abi;

// ---- ShieldedUSD0 ABI (verified, from the mainnet deploy) ----
// USD0 is a public ERC-20 dollar backed one for one by USDS held as sUSDS.
// mint(usdsAmount, receiver) issues USD0 one for one; redeem(usd0Amount,
// minUsdsOut, receiver) burns the caller's USD0 for USDS one for one. Neither
// uses a zero-knowledge proof. convertToAssets and convertToShares are pure
// 1:1; surplusAssets is the sUSDS yield held as backing (it does not accrue
// to holders). Making USD0 private is the separate, optional pool flow.
export const shieldedUsd0Abi = [
  { type: "function", name: "name", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", name: "totalAssets", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "totalSupply", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "surplusAssets", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "convertToAssets", stateMutability: "pure", inputs: [{ name: "shares", type: "uint256" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "convertToShares", stateMutability: "pure", inputs: [{ name: "assets", type: "uint256" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "balanceOf", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "allowance", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "approve", stateMutability: "nonpayable", inputs: [{ name: "spender", type: "address" }, { name: "value", type: "uint256" }], outputs: [{ type: "bool" }] },
  { type: "function", name: "pool", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  {
    type: "function",
    name: "mint",
    stateMutability: "nonpayable",
    inputs: [
      { name: "usdsAmount", type: "uint256" },
      { name: "receiver", type: "address" },
    ],
    outputs: [{ name: "usd0Out", type: "uint256" }],
  },
  {
    type: "function",
    name: "redeem",
    stateMutability: "nonpayable",
    inputs: [
      { name: "usd0Amount", type: "uint256" },
      { name: "minUsdsOut", type: "uint256" },
      { name: "receiver", type: "address" },
    ],
    outputs: [{ name: "usdsOut", type: "uint256" }],
  },
  // Errors, so a revert decodes to a useful message in the dapp.
  { type: "error", name: "AmountTooSmall", inputs: [] },
  { type: "error", name: "ZeroAmount", inputs: [] },
  { type: "error", name: "ZeroAddress", inputs: [] },
  { type: "error", name: "PoolNotSet", inputs: [] },
  { type: "error", name: "ReentrancyGuardReentrantCall", inputs: [] },
  { type: "error", name: "Slippage", inputs: [{ name: "got", type: "uint256" }, { name: "min", type: "uint256" }] },
  { type: "error", name: "SafeERC20FailedOperation", inputs: [{ name: "token", type: "address" }] },
  { type: "error", name: "ERC20InsufficientBalance", inputs: [{ name: "sender", type: "address" }, { name: "balance", type: "uint256" }, { name: "needed", type: "uint256" }] },
  { type: "error", name: "ERC20InsufficientAllowance", inputs: [{ name: "spender", type: "address" }, { name: "allowance", type: "uint256" }, { name: "needed", type: "uint256" }] },
] as const satisfies Abi;

// ---- sUSDS (ERC-4626) read used to value the lender pool ----
// The lender pool holds its idle backing as sUSDS, so converting the
// pool's sUSDS balance to USDS is part of the pool's total value (TVL),
// which sets the USDS worth of a supplier's shares.
export const sUsdsAbi = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "convertToAssets",
    stateMutability: "view",
    inputs: [{ name: "shares", type: "uint256" }],
    outputs: [{ type: "uint256" }],
  },
] as const satisfies Abi;

// ============================================================
// Bonding-curve math (handoff section 5). Exponential curve.
//   price(s) = P0 * (Pf/P0)^(s / Smax) = P0 * 20^(s / Smax)
//   reserve(s) = (price(s) - P0) / lambda,  lambda = ln(20) / Smax
//
// P0, PF, S_MAX, and RESERVE_AT_BOND below are the source's stated
// ground-truth figures, rounded for display (PF 0.303 is the rounded
// 20x of P0 0.0152, whose exact product is 0.304). They are kept
// verbatim, so the closed-form reserve at S_MAX is near but not
// exactly RESERVE_AT_BOND. The canonical trigger is the SUPPLY: the
// contract closes issuance once minted reaches S_MAX (bonded() flips
// true at that block). minted is the gross counter, so burned 01
// counts toward it too (minted = circulating supply + total burned).
// RESERVE_AT_BOND is only what the reserve equals at that point by
// calibration; because the reserve earns sUSDS yield it can drift
// above $2,000,000 without bonding, since bonding watches minted, not
// the dollar balance.
// ============================================================

export const CURVE = {
  CAP: 21_000_000, // hard cap on 01
  S_MAX: 20_790_000, // sellable tokens minted by bonding
  P0: 0.0152, // price of the first token, USD
  PF: 0.303, // price of the last token, USD (20x the first)
  MULTIPLE: 20,
  FEE_BPS: 25, // 0.25% on every buy and sell, burned
  RESERVE_AT_BOND: 2_000_000, // USDS
} as const;

export const LAMBDA = Math.log(CURVE.MULTIPLE) / CURVE.S_MAX;
export const FEE_RATE = CURVE.FEE_BPS / 10_000;

/** Spot price at a given minted supply s. */
export function priceAt(s: number): number {
  return CURVE.P0 * Math.pow(CURVE.MULTIPLE, s / CURVE.S_MAX);
}

/** Reserve held at a given minted supply s. */
export function reserveAt(s: number): number {
  return (priceAt(s) - CURVE.P0) / LAMBDA;
}

/** Inverse of reserveAt: minted supply that produces a given reserve. */
export function supplyAtReserve(reserve: number): number {
  return (CURVE.S_MAX * Math.log((reserve * LAMBDA + CURVE.P0) / CURVE.P0)) /
    Math.log(CURVE.MULTIPLE);
}

/** Reserve backing each circulating token. */
export function reservePerToken(reserve: number, supply: number): number {
  return supply > 0 ? reserve / supply : 0;
}

/** Quoted market cap (price x supply). Always shown next to the reserve, never alone. */
export function quotedMarketCap(price: number, supply: number): number {
  return price * supply;
}
